import React from 'react';
import Box from '@mui/material/Box';
import Header from './components/Header';
import GridContainer from '../../../@jumbo/components/GridContainer';
import Grid from '@mui/material/Grid';
import Contact from './components/Contact';
import { useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Switch, useHistory, Route } from 'react-router';
import { AccountBox, Dashboard, ShoppingBasket } from '@mui/icons-material';
import ProfileDetails from './components/ProfileDetails';
import ProfileRequestHistories from './components/ProfileRequestHistories';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import ProfilePreviousProjects from './components/ProfilePreviousProjects';
import ProfilePreviousProject from './components/ProfilePreviousProject';
import useWidth from 'services/Hooks/useWidth';
import ProfileAwaitingRequests from './components/ProfileAwaitingRequests';
import OrderDetail from './components/OrderDetail';
import ExteriorSignageQuoteCheckout from '../ExteriorSignageQuote/ExteriorSignageQuoteCheckout';
import MerchandisingProgramQuoteCheckout from '../MerchandisingProgramQuote/MerchandisingProgramQuoteCheckout';
import { Slide } from '@mui/material';
import AssociatedCartSupportChats from '../Support/AssociatedCartSupportChats';
import AssociatedExteriorSignageQuoteSupportChats from '../Support/AssociatedExteriorSignageQuoteSupportChats';
import AssociatedMerchandisingProgramQuoteSupportChats from '../Support/AssociatedMerchandisingProgramQuoteSupportChats';
import ProfileDsmDashboard from './components/ProfileDsmDashboard';
import { ShowDSMDashboard } from 'config/config';

const useStyles = makeStyles(theme => ({
  pageFull: {
    width: '100%',
    paddingTop: 30,
    [theme.breakpoints.up('md')]: {
      paddingLeft: 30,
      paddingRight: 30,
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 30,
      paddingRight: 30,
    },
    [theme.breakpoints.up('xl')]: {
      paddingLeft: 30,
      paddingRight: 30,
    },
  },
  profileMainContent: {
    zIndex: 10,
  },
}));

const Profile = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const width = useWidth();

  const accountReducer = useSelector(state => state.AccountReducer);

  const { profile } = accountReducer;

  const defaultTabRoute = ShowDSMDashboard ? 'dsm-dashboard' : 'awaiting-requests';

  const tabs = [
    ...(ShowDSMDashboard
      ? [
          {
            id: 1,
            title: t('dsmDashboard.tabTitle'),
            tabRoute: 'dsm-dashboard',
            icon: <Dashboard />,
          },
        ]
      : []),
    {
      id: ShowDSMDashboard ? 2 : 1,
      title: t('pendingRequests'),
      tabRoute: 'awaiting-requests',
      icon: <ShoppingBasket />,
    },
    {
      id: ShowDSMDashboard ? 3 : 2,
      title: t('requestHistories'),
      tabRoute: 'request-histories',
      icon: <ShoppingBasket />,
    },
    {
      id: ShowDSMDashboard ? 4 : 3,
      title: t('previousProjects'),
      tabRoute: 'previous-projects',
      icon: <ApplicationIcons.Project />,
    },
    {
      id: ShowDSMDashboard ? 5 : 4,
      title: t('popup.profile'),
      tabRoute: 'identification',
      icon: <AccountBox />,
    },
  ];

  const activeTab =
    tabs.find(tab => history.location.pathname.includes(`/${tab.tabRoute}`))?.id ??
    tabs.find(tab => tab.tabRoute === defaultTabRoute)?.id ??
    tabs[0].id;

  const changeUrl = index => {
    const routeName = tabs[index - 1].tabRoute;
    history.push(`/dealer/profile/${routeName}`);
  };

  const handleTabChange = (event, newValue) => {
    changeUrl(newValue);
  };

  const routeUsingFullWidth =
    history.location.pathname.includes('/previous-projects/') ||
    (ShowDSMDashboard &&
      (history.location.pathname === '/dealer' ||
        history.location.pathname === '/dealer/profile' ||
        history.location.pathname.includes('/dsm-dashboard')));

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      {profile && (
        <Box className={classes.pageFull}>
          <Header userDetail={profile} tabValue={activeTab} handleTabChange={handleTabChange} tabs={tabs} />
          <GridContainer>
            {(width === 'xs' || !routeUsingFullWidth) && (
              <Grid item xs={12} md={4} className={classes.profileSidebar}>
                <Box mb={6}>
                  <Contact userDetail={profile} />
                </Box>
                <Box mb={6}>
                  <Switch>
                    <Route
                      path="/dealer/profile/awaiting-requests/order/:cartId"
                      exact
                      render={() => <AssociatedCartSupportChats />}
                    />
                    <Route
                      path="/dealer/profile/awaiting-requests/exterior-signage-quote/:exteriorSignageQuoteId"
                      exact
                      render={() => <AssociatedExteriorSignageQuoteSupportChats />}
                    />
                    <Route
                      path="/dealer/profile/awaiting-requests/merchandising-program-quote/:merchandisingProgramQuoteId"
                      exact
                      render={() => <AssociatedMerchandisingProgramQuoteSupportChats />}
                    />

                    <Route
                      path="/dealer/profile/request-histories/order/:cartId"
                      exact
                      render={() => <AssociatedCartSupportChats disableSupportChat />}
                    />
                    <Route
                      path="/dealer/profile/request-histories/exterior-signage-quote/:exteriorSignageQuoteId"
                      exact
                      render={() => <AssociatedExteriorSignageQuoteSupportChats disableSupportChat />}
                    />
                    <Route
                      path="/dealer/profile/request-histories/merchandising-program-quote/:merchandisingProgramQuoteId"
                      exact
                      render={() => <AssociatedMerchandisingProgramQuoteSupportChats disableSupportChat />}
                    />
                  </Switch>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={routeUsingFullWidth ? 12 : 8} className={classes.profileMainContent}>
              <Box mb={6}>
                <Switch>
                  <Route
                    path="/dealer"
                    exact
                    render={() => (ShowDSMDashboard ? <ProfileDsmDashboard /> : <ProfileAwaitingRequests />)}
                  />
                  <Route
                    path="/dealer/profile"
                    exact
                    render={() => (ShowDSMDashboard ? <ProfileDsmDashboard /> : <ProfileAwaitingRequests />)}
                  />
                  <Route path="/dealer/profile/dsm-dashboard" exact render={() => <ProfileDsmDashboard />} />
                  <Route path="/dealer/profile/request-histories" exact render={() => <ProfileRequestHistories />} />
                  <Route
                    path="/dealer/profile/request-histories/order/:cartId"
                    exact
                    render={() => <OrderDetail fromHistory fromProfile />}
                  />
                  <Route
                    path="/dealer/profile/request-histories/exterior-signage-quote/:exteriorSignageQuoteId"
                    exact
                    render={() => <ExteriorSignageQuoteCheckout fromHistory fromProfile />}
                  />

                  <Route
                    path="/dealer/profile/request-histories/merchandising-program-quote/:merchandisingProgramQuoteId"
                    exact
                    render={() => <MerchandisingProgramQuoteCheckout fromHistory fromProfile />}
                  />

                  <Route path="/dealer/profile/awaiting-requests" exact render={() => <ProfileAwaitingRequests />} />
                  <Route
                    path="/dealer/profile/awaiting-requests/order/:cartId"
                    exact
                    render={() => <OrderDetail fromProfile />}
                  />
                  <Route
                    path="/dealer/profile/awaiting-requests/exterior-signage-quote/:exteriorSignageQuoteId"
                    exact
                    render={() => <ExteriorSignageQuoteCheckout fromProfile />}
                  />
                  <Route
                    path="/dealer/profile/awaiting-requests/merchandising-program-quote/:merchandisingProgramQuoteId"
                    exact
                    render={() => <MerchandisingProgramQuoteCheckout fromProfile />}
                  />

                  <Route path="/dealer/profile/previous-projects" exact render={() => <ProfilePreviousProjects />} />
                  <Route
                    path="/dealer/profile/previous-projects/:projectId"
                    exact
                    render={() => <ProfilePreviousProject />}
                  />
                  <Route path="/dealer/profile/identification" exact render={() => <ProfileDetails />} />
                </Switch>
              </Box>
            </Grid>
          </GridContainer>
        </Box>
      )}
    </Slide>
  );
};

export default Profile;
